import type { ChangeEvent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { DashboardSettingsResponseDto } from '@api'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Page } from '@/components/layout/page/page.tsx'
import { Toggle } from '@/components/ui/Toggle.tsx'
import { FormNumberField } from '@/components/ui/form/form-number-field'
import { TextField } from '@/components/ui/text-field.tsx'
import { useToast } from '@/components/ui/toast.tsx'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider.tsx'
import { SettingsOption } from '@/modules/creator-settings/ui/SettingsOption'
import { debounce } from '@/utils/helpers'

import { useAutomationsMutations } from '../../hooks/use-automations-mutations'

interface AutoUnFollowFormProps {
  data: Pick<
    DashboardSettingsResponseDto,
    | 'autoUnfollowEnabled'
    | 'autoUnfollowMaximumSpend'
    | 'autoUnfollowAfterFollowingDays'
  >
}

type AutoUnFollowFormValues = z.infer<typeof AutoUnFollowFormSchema>

const AutoUnFollowFormSchema = z.object({
  enabled: z.boolean(),
  spend: z.number(),
  days: z.number(),
})

export const AutoUnFollowForm = ({ data }: AutoUnFollowFormProps) => {
  const {
    setAutoUnFollowMinimumSpend,
    enableAutoUnFollow,
    setAutoUnFollowDays,
  } = useAutomationsMutations()

  const { setSettingsData } = useSettingsContext()

  const toast = useToast()

  const [maximumSpendEnabled, setMaximumSpendEnabled] = useState(
    data.autoUnfollowMaximumSpend! > 0,
  )

  const defaultFormValues = useMemo(
    () => ({
      enabled: data.autoUnfollowEnabled,
      spend: data.autoUnfollowMaximumSpend,
      days: data.autoUnfollowAfterFollowingDays,
    }),
    [data],
  )

  const form = useForm<AutoUnFollowFormValues>({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
  })

  const handleEnabledChange = useCallback(
    (value: ChangeEvent<HTMLInputElement>) => {
      const checkedState = value.target.checked

      enableAutoUnFollow
        .mutateAsync({
          state: checkedState,
        })
        .then(() => {
          let state = 'enabled'

          if (checkedState === false) {
            state = 'disabled'
          }

          setSettingsData((prevState) => ({
            ...prevState,
            autoUnfollowEnabled: checkedState,
          }))

          toast.success(`Successfully ${state} Auto Un-Follow`)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedSpendingChange = useCallback(
    debounce((targetValue: string | number) => {
      const value = parseInt(targetValue.toString())

      if (isNaN(value) || value < 0) {
        return
      }

      if (value == 0) {
        setMaximumSpendEnabled(false)
      }

      setAutoUnFollowMinimumSpend.mutateAsync(value).then(() => {
        form.setValue('spend', value)
        setSettingsData((prevState) => ({
          ...prevState,
          autoUnfollowMaximumSpend: value,
        }))

        toast.success('Successfully set un-follow spending limitations')
      })
    }, 800),
    [],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDebouncedDaysChange = useCallback(
    debounce(setAutoUnFollowDays, 800),
    [],
  )

  return (
    <FormProvider {...form}>
      <Page
        title="Auto Un-Follow"
        pageClassName="py-4"
        description={
          <div className="flex items-center gap-1">
            <span>
              Automatically un-follow fans that you (or the Auto-Follow feature)
              have followed before.
            </span>
          </div>
        }
        headerOptions={
          <Controller
            name="enabled"
            control={form.control}
            render={({ field }) => (
              <Toggle
                name="unFollowEnabled"
                checked={field.value}
                onChange={(value) => {
                  field.onChange(value)
                  handleEnabledChange(value)
                }}
              />
            )}
          />
        }
      >
        <hr />

        <SettingsOption
          title="Spending Limitations"
          description="Only un-follow fans with a total spend below the chosen threshold"
          biggerTitle
          mobileVertical
        >
          <div className="flex items-center gap-4">
            {maximumSpendEnabled && (
              <Controller
                name="spend"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    className="w-32"
                    type="number"
                    value={field.value}
                    prefix={
                      <span className="text-text-sub flex items-center">$</span>
                    }
                    onChange={(value) => {
                      field.onChange(value)
                      handleDebouncedSpendingChange(value.target.value)
                    }}
                    onBlur={(event) => {
                      if (event.target.value === '') {
                        form.setValue('spend', data.autoUnfollowMaximumSpend!)
                      }
                    }}
                  />
                )}
              />
            )}
            <Toggle
              name="maximumSpendEnabled"
              checked={maximumSpendEnabled}
              onChange={(value) => {
                const checked = value.target.checked

                setMaximumSpendEnabled(checked)
                if (checked) {
                  form.setValue('spend', 200)
                  handleDebouncedSpendingChange(200)
                  // handleEnabledChange(value);
                } else {
                  form.setValue('spend', 0)
                  handleDebouncedSpendingChange(0)
                }
              }}
            />
          </div>
        </SettingsOption>
        <hr />
        <SettingsOption
          title="Set Period"
          description="Enter the minimum number of days before un-following fans after you‘ve followed them"
          biggerTitle
          mobileVertical
        >
          <FormNumberField
            postfix="Days"
            name="days"
            onValueChange={handleDebouncedDaysChange}
          />
        </SettingsOption>
      </Page>
    </FormProvider>
  )
}
